import { useId } from "react";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { ConsoleService } from "@yatayat/gen/yatayat/console/v1/console_pb";

import { AuditChainStatus, useAuditChain } from "~/components/console/AuditChainStatus";
import { AuditLogTable, auditKindLabels } from "~/components/console/AuditLogTable";
import { FilterSelect, Pager, usePageTokens } from "~/components/console/ComplianceKit";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type AuditSearch = { entity?: string };

export const Route = createFileRoute("/console/audit")({
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    entity: searchString(search.entity),
  }),
  component: ConsoleAudit,
});

const pageSize = 50;
const entityKind = /^[a-z_]{1,64}$/;

function ConsoleAudit() {
  const { t } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const logHeading = useId();

  const entity = search.entity && entityKind.test(search.entity) ? search.entity : "";
  const pages = usePageTokens(entity);
  const chain = useAuditChain();
  const log = useQuery(
    ConsoleService.method.listAuditLog,
    { entityKind: entity, pageSize, pageToken: pages.token },
    { retry: retryTransient, placeholderData: keepPreviousData },
  );
  const unauthenticated = useStaffSessionExpiry(log.error);
  const entries = log.data?.entries ?? [];
  const firstBadId = chain.data && !chain.data.intact ? chain.data.firstBadId : 0n;

  // The known kinds, plus any others this page shows, plus the one filtered on.
  const kinds = [...new Set([...Object.keys(auditKindLabels), ...entries.map((e) => e.entityKind), ...(entity ? [entity] : [])])];
  const kindOptions = [
    { value: "", label: t("auditAllEntities") },
    ...kinds.map((k) => ({ value: k, label: Object.hasOwn(auditKindLabels, k) ? t(auditKindLabels[k]) : k })),
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className={ui.h1}>{t("auditTitle")}</h1>
        <p className={ui.muted}>{t("auditIntro")}</p>
      </div>

      <AuditChainStatus chain={chain} />

      <section aria-labelledby={logHeading} className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 id={logHeading} className={ui.h2}>
            {t("auditLogTitle")}
          </h2>
          <div className="w-64">
            <FilterSelect
              label={t("auditEntity")}
              value={entity}
              onChange={(v) => void navigate({ search: { entity: v || undefined }, replace: true, resetScroll: false })}
              options={kindOptions}
            />
          </div>
        </div>

        {log.isPending && <p className={ui.muted}>{t("loading")}</p>}
        {log.isError && !log.data && !unauthenticated && <QueryError onRetry={() => void log.refetch()} busy={log.isFetching} />}
        {log.data && entries.length === 0 && <p className={ui.empty}>{t("auditEmpty")}</p>}
        {entries.length > 0 && (
          <div aria-busy={log.isFetching} className={log.isPlaceholderData ? "opacity-60" : undefined}>
            <AuditLogTable entries={entries} firstBadId={firstBadId} />
          </div>
        )}
        <Pager page={pages.page} nextToken={log.data?.nextPageToken ?? ""} busy={log.isFetching} onPrevious={pages.previous} onNext={pages.next} />
      </section>
    </div>
  );
}
